import { Injectable } from '@angular/core'
import { environment } from '../../environments/environment'
import { HttpClient } from '@angular/common/http'
import { catchError, switchMap, takeWhile, map } from 'rxjs/operators'
import { throwError } from 'rxjs'
import { IngestContainerModel } from '../models/ingest-container.model'
import { BlobServiceClient, ContainerClient, BlockBlobClient } from '@azure/storage-blob'
import { Router } from '@angular/router'
import { BsModalService } from 'ngx-bootstrap/modal'
import { Observable, Subscription } from 'rxjs'

@Injectable({
  providedIn: 'root'
})
export class PictureParkService {

  private apiUrl = environment.api.url + environment.api.picturePark.url
  private pollingSubscription: Subscription

  constructor(private http: HttpClient, private router: Router, private modalService: BsModalService) {}

  initializeFileUpload(file: Blob, document: any): Observable<any> {
    return this.createIngestContainer().pipe(
      switchMap(async (response: IngestContainerModel) => {
        try {
          // Create BlobServiceClient using SAS token
          const blobServiceClient = new BlobServiceClient(response.sasToken)
          const containerClient = blobServiceClient.getContainerClient(response.containerName)

          const blobName = document.displayName
          const blockBlobClient = containerClient.getBlockBlobClient(blobName)

          // Upload file with modern SDK
          const uploadBlobResponse = await blockBlobClient.upload(file, file.size, {
            blobHTTPHeaders: {
              blobContentType: file.type || 'application/octet-stream'
            }
          })

          console.log('Upload successful:', uploadBlobResponse.requestId)

          // Start the upload processing
          const businessProcess = await this.startUpload(response.containerName).toPromise()
          if (businessProcess) {
            this.isUploadFinished(businessProcess.id, document)
          }
          return { success: true, uploadId: uploadBlobResponse.requestId }
        } catch (error) {
          console.error('Azure upload error:', error)
          throw error
        }
      }),
      catchError(err => throwError(() => err))
    )

    /* COMMENTED OUT UNTIL AZURE STORAGE IS UPDATED
    return this.createIngestContainer().subscribe({
      next: async (response: any) => {
        const containerName = response.containerName
        const blobName = document.displayName
        // const blobService: any = (azure as any).createBlobServiceWithSas(response.sasToken)

        const options = {
          contentSettings: {
            contentType: file.type || 'application/octet-stream'
          }
        }

        // Check file size and use appropriate upload method
        const maxTextSize = 32 * 1024 * 1024 // 32MB threshold - use chunked upload for large files

        if (file.size > maxTextSize) {
          // For large files, we need to use a different approach
          // Convert to base64 and upload in chunks using putBlock + putBlockList
          this.uploadLargeFile(blobService, containerName, blobName, file, options, document)
        } else {
          // For small files, use the direct approach
          const reader = new FileReader()
          reader.onload = () => {
            const base64Data = (reader.result as string).split(',')[1] // Remove data:type;base64, prefix

            blobService.createBlockBlobFromText(containerName, blobName, base64Data, options, (error: any) => {
              if (!error) {
                this.startUpload(containerName).subscribe({
                  next: (businessProcess: any) => {
                    if (businessProcess) {
                      this.isUploadFinished(businessProcess.id, document)
                    }
                  }
                })
              } else {
                console.error('Azure blob upload error:', error)
                // Hide loader on error
                if ((window as any).uploadLoaderRef) {
                  (window as any).uploadLoaderRef.hide()
                  delete (window as any).uploadLoaderRef
                }
              }
            })
          }
          reader.readAsDataURL(file) // Read as base64 data URL for legacy SDK
        }
      }
    })
    */
  }

  /* COMMENTED OUT UNTIL AZURE STORAGE IS UPDATED
  private uploadLargeFile(blobService: any, containerName: string, blobName: string, file: Blob, options: any, document: any) {
    // For large files, split into 8MB chunks and use putBlock + putBlockList
    const chunkSize = 8 * 1024 * 1024 // 8MB chunks (optimal for 150-900MB files)
    const totalChunks = Math.ceil(file.size / chunkSize)
    const blockIds: string[] = []
    let uploadedChunks = 0

    const uploadChunk = (chunkIndex: number) => {
      const start = chunkIndex * chunkSize
      const end = Math.min(start + chunkSize, file.size)
      const chunk = file.slice(start, end)

      // Generate block ID (must be base64 encoded and same length)
      const blockId = btoa(chunkIndex.toString().padStart(6, '0'))
      blockIds[chunkIndex] = blockId

      const reader = new FileReader()
      reader.onload = () => {
        const base64Data = (reader.result as string).split(',')[1] // Remove data:type;base64, prefix

        blobService.createBlockFromText(blockId, containerName, blobName, base64Data, (error: any) => {
          if (!error) {
            uploadedChunks++
            console.log(`Uploaded chunk ${uploadedChunks}/${totalChunks} (${Math.round(uploadedChunks/totalChunks*100)}%)`)

            // If all chunks uploaded, commit the block list
            if (uploadedChunks === totalChunks) {
              console.log('All chunks uploaded, committing blocks...')
              blobService.commitBlocks(containerName, blobName, blockIds, options, (error: any) => {
                if (!error) {
                  this.startUpload(containerName).subscribe({
                    next: (businessProcess: any) => {
                      if (businessProcess) {
                        this.isUploadFinished(businessProcess.id, document)
                      }
                    }
                  })
                } else {
                  console.error('Azure blob commit error:', error)
                  // Hide loader on error
                  if ((window as any).uploadLoaderRef) {
                    (window as any).uploadLoaderRef.hide()
                    delete (window as any).uploadLoaderRef
                  }
                }
              })
            } else if (chunkIndex + 1 < totalChunks) {
              // Upload next chunk
              uploadChunk(chunkIndex + 1)
            }
          } else {
            console.error('Azure blob chunk upload error:', error)
            // Hide loader on error
            if ((window as any).uploadLoaderRef) {
              (window as any).uploadLoaderRef.hide()
              delete (window as any).uploadLoaderRef
            }
          }
        })
      }
      reader.readAsDataURL(chunk) // Read as base64 data URL for legacy SDK
    }

    // Start uploading first chunk
    uploadChunk(0)
  }
  */

  /** Upload step 1 : create an azure blob storage container and put file into it **/
  createIngestContainer(): Observable<IngestContainerModel> {
    return this.http.get(this.apiUrl).pipe(
      map((response: any) => {
        const { sasToken, containerName } = response
        return new IngestContainerModel(sasToken, containerName)
      }),
      catchError(err => throwError(() => err))
    )
  }

  /** Upload step 2: Import all files into pictures park from container name **/
  startUpload(containerName: string): Observable<any> {
    return this.http.get(this.apiUrl + `/upload/start?containerName=${containerName}`)
      .pipe(
        map((response: any) => {
          return response
        }),
        catchError(err => throwError(() => err))
      )
  }

  /** Upload step 3: Call picture park multiple times to get the status of the current upload **/
  isUploadFinished(businessProcessId: string, document: any): void {
    this.pollingSubscription = new Observable((observer: any) => {
      const intervalId = setInterval(() => {
        observer.next();
      }, 5000);
      return () => clearInterval(intervalId);
    })
      .pipe(
        switchMap(() => this.http.get<any>(this.apiUrl + `/upload/status?businessProcessId=${businessProcessId}`)),
        takeWhile((response: boolean) => {
          if (response) this.completeUpload(document).subscribe()
          return !response
        })
      ).subscribe((response: boolean) => {
        return response
      })
  }

  stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  /** Upload step 4: Saving document information into DB **/
  completeUpload(document: any): Observable<any> {
    return this.http.post(this.apiUrl, document).pipe(
      map((res: any) => {
        // Hide the upload loader
        if ((window as any).uploadLoaderRef) {
          (window as any).uploadLoaderRef.hide()
          delete (window as any).uploadLoaderRef
        }
        this.modalService.hide(1);
        if (res && res.id) {
          this.stopPolling()
          this.router.navigateByUrl(`admin/competition/${res.parentId}/file/${res.id}`)
        }
        return res
      })
    )
  }
}
